type Meta = {
  role_permission_id?: number;
  api_key_permission_id?: number;
};
export type Permission = {
  id: string;
  name: string;
  description: string;
  entity: string;
  path: string;
  inserted_at: Date;
  updated_at: Date;
  ability: string;
  actions: string[] | any[];
  order: number;
  icon: string;
  capability_type: string;
  meta: Meta;
  /** The catalog code roles hold it by (`rentals`, `rentals:approve`, …). */
  code?: string;
  /**
   * No longer in the server's permission sheet: Settings → Roles hides it,
   * but a role that holds it keeps it.
   */
  retired?: boolean;
};

export enum PermissionEntity {
  DASHBOARD = "/",
  CATEGORIES = "/categories",
  PRODUCTS = "/products",
  USERS = "/users",
  ROLES = "/roles",
  PERMISSIONS = "/permissions",
  API_KEYS = "/api_keys",
  VEHICLES = "vehicles",
  WORK_ORDERS = "/work_orders",
  INVENTORY = "/inventory",
  RATE_PLANS = "/rate_plans",
  PRICING_RULES = "/pricing_rules",
  RATE_MODIFIERS = "/rate_modifiers",
  QUOTES = "/quotes",
  RENTALS = "/rentals",
  INBOX = "/inbox",
}

export enum PermissionAction {
  MODIFY = "modify",
  READ = "read",

  CREATE = "create",
  UPDATE = "update",
  DELETE = "delete",
  SKIP = "skip",
}

export type PermissionMap = {
  [PermissionEntity.CATEGORIES]: PermissionAction[];
  [PermissionEntity.PRODUCTS]: [...PermissionAction[], "publish"]; // extra
  [PermissionEntity.USERS]: [...PermissionAction[], "assign"];
  [PermissionEntity.ROLES]: PermissionAction[];
  [PermissionEntity.PERMISSIONS]: PermissionAction[];
  [PermissionEntity.API_KEYS]: [...PermissionAction[], "revoke"];
  [PermissionEntity.VEHICLES]: PermissionAction[];
  [PermissionEntity.WORK_ORDERS]: PermissionAction[];
  [PermissionEntity.INVENTORY]: [PermissionAction.READ]; // only read
  [PermissionEntity.RATE_PLANS]: PermissionAction[];
  [PermissionEntity.PRICING_RULES]: PermissionAction[];
  [PermissionEntity.RATE_MODIFIERS]: PermissionAction[];
  [PermissionEntity.QUOTES]: PermissionAction[];
  [PermissionEntity.RENTALS]: PermissionAction[];
  [PermissionEntity.INBOX]: PermissionAction[];
  [PermissionEntity.DASHBOARD]: [];
};

export type EntityActionPair<E extends PermissionEntity> = {
  entity: E;
  action: PermissionMap[E][number];
};

export type PermissionProps = {
  entity: string;
  action: string;
};

// Tree structure types for permissions with actions
export type PermissionActionItem = {
  id: string;
  name: string;
  description?: string;
};

export type PermissionTreeNode = Permission & {
  children?: PermissionTreeNode[];
};

export type PermissionTree = {
  data: PermissionTreeNode[];
};

/**
 * The server issues `rentals:approve` (approve or decline a Request to Book
 * booking request) as its own permission, keyed by the path
 * `/reservations/approve` with the `modify` action. It is a sibling of
 * `/reservations`, not one of its actions, so check it with `strictMatch`:
 * without it, `canPerform` falls back to the `/reservations` permission and
 * every user who can modify reservations would pass. Fleet owners always pass.
 */
export const APPROVE_BOOKING_REQUESTS_PERMISSION = {
  entity: "/reservations/approve",
  action: PermissionAction.MODIFY,
} as const;

/**
 * The server issues `rentals:record_external_payment` (record and edit a
 * payment received outside FleetSync) and `rentals:void_external_payment`
 * (void one) as their own permissions, keyed by the paths below with the
 * `modify` action. Like `APPROVE_BOOKING_REQUESTS_PERMISSION`, they are
 * siblings of `/reservations`, so check them with `strictMatch`: without it,
 * `canPerform` falls back to the `/reservations` permission and every user
 * who can modify reservations would pass. Fleet owners always pass.
 */
export const RECORD_EXTERNAL_PAYMENTS_PERMISSION = {
  entity: "/reservations/record-external-payment",
  action: PermissionAction.MODIFY,
} as const;

export const VOID_EXTERNAL_PAYMENTS_PERMISSION = {
  entity: "/reservations/void-external-payment",
  action: PermissionAction.MODIFY,
} as const;

/**
 * The server issues `booking_questions_settings` as an ordinary settings
 * child (`FmsApi.PermissionSheet`, `capability_type: :menu`, `parent_code:
 * "settings"`, `path: "/settings/booking-questions"`) — the same shape as
 * every other real settings page (`/settings/insurance`,
 * `/settings/security-deposits`, ...), not a sibling action permission like
 * `APPROVE_BOOKING_REQUESTS_PERMISSION` above. No `strictMatch` is needed
 * when checking it: unlike `/reservations/approve`, this path has no
 * `/settings/booking-questions/something` child it could be confused with.
 */
export const BOOKING_QUESTIONS_SETTINGS_PERMISSION = {
  entity: "/settings/booking-questions",
  action: PermissionAction.MODIFY,
} as const;

/**
 * Editing Settings > Payments. The server issues `payments_settings` as an
 * ordinary settings child (`path: "/settings/payments"`) and also lets
 * `settings` (modify) edit it, so the base-route fallback of `canPerform` is
 * wanted here: no `strictMatch`.
 */
export const PAYMENTS_SETTINGS_PERMISSION = {
  entity: "/settings/payments",
  action: PermissionAction.MODIFY,
} as const;
